import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Wifi,
  WifiOff,
  Speed
} from '@mui/icons-material';
import { useSocket } from '../context/SocketContext';
import { useUnifiedMessaging } from '../context/UnifiedMessagingContext';
import api from '../api/api';

interface ConnectionStatus {
  socketIO: {
    connected: boolean;
    clientCount: number;
  };
  configuration: {
    realtimeProvider: string;
  };
}

const MessagingDiagnostics: React.FC = () => {
  const { connected: socketConnected, emit, on, off } = useSocket();
  const { preferredProtocol } = useUnifiedMessaging();
  
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [testRoom, setTestRoom] = useState('test:room');
  const [testMessage, setTestMessage] = useState('');
  const [receivedMessages, setReceivedMessages] = useState<any[]>([]);
  const [latency, setLatency] = useState<number | null>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    // Listen for test messages
    const handleTestMessage = (data: any) => {
      setReceivedMessages(prev => [{
        ...data,
        receivedAt: new Date().toISOString(),
        protocol: 'Socket.IO'
      }, ...prev.slice(0, 9)]);
    };

    on('test', handleTestMessage);

    return () => {
      off('test', handleTestMessage);
    };
  }, [on, off]);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/diagnostics/messaging/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch messaging status:', error);
    } finally {
      setLoading(false);
    }
  };

  const testSocketIO = () => {
    const startTime = Date.now();
    api.post('/diagnostics/messaging/test/socketio', {
      room: testRoom,
      event: 'test',
      message: testMessage || 'Test message from diagnostics'
    }).then(() => {
      const endTime = Date.now();
      setLatency(endTime - startTime);
      setTestMessage('');
    }).catch(error => {
      console.error('Socket.IO test failed:', error);
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Messaging System Diagnostics
      </Typography>

      <Grid container spacing={3}>
        {/* Connection Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Socket.IO Status
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    {socketConnected ? <Wifi color="success" /> : <WifiOff color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary="Connection"
                    secondary={socketConnected ? 'Connected' : 'Disconnected'}
                  />
                  <Chip
                    label={socketConnected ? 'Active' : 'Inactive'}
                    color={socketConnected ? 'success' : 'error'}
                    size="small"
                  />
                </ListItem>
                {status && (
                  <ListItem>
                    <ListItemIcon>
                      <Speed />
                    </ListItemIcon>
                    <ListItemText
                      primary="Connected Clients"
                      secondary={status.socketIO.clientCount}
                    />
                  </ListItem>
                )}
                {latency !== null && (
                  <ListItem>
                    <ListItemIcon>
                      <Speed color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Latency"
                      secondary={`${latency}ms`}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Protocol Configuration */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Protocol Configuration
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Current preferred protocol: <strong>{preferredProtocol.toUpperCase()}</strong>
              </Alert>
              {status && (
                <Grid container spacing={2}>
                  {Object.entries(status.configuration).map(([key, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <Box sx={{ p: 1, border: '1px solid var(--border-default)', borderRadius: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </Typography>
                        <Typography variant="body2">
                          <Chip label={value} size="small" color={value === 'SOCKET_IO' ? 'primary' : 'secondary'} />
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Test Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Messaging
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Room Name"
                    value={testRoom}
                    onChange={(e) => setTestRoom(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Test Message"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="contained"
                      onClick={testSocketIO}
                      disabled={!socketConnected}
                      size="small"
                    >
                      Test Socket.IO
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Received Messages */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Received Messages
              </Typography>
              {receivedMessages.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No messages received yet. Try sending a test message.
                </Typography>
              ) : (
                <List>
                  {receivedMessages.map((msg, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={`${msg.protocol}: ${msg.message || JSON.stringify(msg)}`}
                          secondary={`Received at: ${new Date(msg.receivedAt).toLocaleTimeString()}`}
                        />
                      </ListItem>
                      {index < receivedMessages.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MessagingDiagnostics;